import { useState } from "react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Facebook, Twitter, Instagram, Linkedin, Send, MapPin, Phone, Mailbox } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });

  const handleNewsletter = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Subscribed with:", email);
    setEmail("");
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Contact form submitted:", contactForm);
    setContactForm({ name: "", email: "", message: "" });
  };

  const services = [
    { name: "Software Development", href: "/software-development" },
    { name: "Web Development", href: "/web-design-development" },
    { name: "Mobile Apps", href: "/mobile-app-development" },
    { name: "Digital Marketing", href: "/digital-marketing" },
    { name: "eCommerce", href: "/ecommerce" },
  ];

  const company = [
    { name: "About Us", href: "/about" },
    { name: "Careers", href: "/careers" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
    { name: "Privacy Policy", href: "/privacy-policy" },
    { name: "Terms of Service", href: "/terms-of-service" },
    { name: "Cookie Policy", href: "/cookie-policy" },
  ];

  const socialLinks = [
    { name: "Facebook", href: "#", icon: Facebook },
    { name: "Twitter", href: "#", icon: Twitter },
    { name: "Instagram", href: "#", icon: Instagram },
    { name: "LinkedIn", href: "#", icon: Linkedin },
  ];

  const contactInfo = [
    { icon: MapPin, text: "Sharjah, UAE" },
    { icon: Phone, text: "+971 6 123 4567" },
    { icon: Mailbox, text: "info@zeexsoft.com" },
  ];

  return (
    <footer className="bg-background/90 border-t border-primary/20 py-16">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-1"
          >
            <div className="flex items-center space-x-3 mb-6 group">
              <div className="relative">
                <img
                  src="https://res.cloudinary.com/due4nhetn/image/upload/v1750568477/Untitled_design_8_hxwmir.png"
                  alt="ZeeX Soft Logo"
                  className="w-12 h-12 rounded-lg object-cover shadow-lg transition-all duration-300 group-hover:shadow-primary/20"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-orbitron font-bold text-2xl bg-clip-text text-transparent bg-[linear-gradient(90deg,var(--primary),var(--primary-foreground))] drop-shadow-[0_0_8px_rgba(99,102,241,0.5)]">ZeeX <span className="text-white">Soft</span></span>
                <span className="text-xs font-medium text-foreground/80 -mt-1 tracking-wider">TECHNOLOGIES</span>
              </div>
            </div>
            <p className="text-muted-foreground mb-4">We Architect Tomorrow's Tech</p>
            <p className="text-sm text-muted-foreground mb-6">
              Leading software development company in Sharjah, UAE, delivering cutting-edge digital solutions.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              {contactInfo.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="text-sm text-muted-foreground">{item.text}</span>
                  </div>
                );
              })}
            </div>
            
            {/* Social Media Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Icon className="h-5 w-5" />
                  </a>
                );
              })}
            </div>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h3 className="font-semibold text-lg mb-6 text-white">Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    href={service.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3 className="font-semibold text-lg mb-6 text-white">Company</h3>
            <ul className="space-y-3">
              {company.map((item) => (
                <li key={item.name}>
                  <Link
                    href={item.href}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="font-semibold text-lg mb-6 text-white">Contact Us</h3>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <Input
                type="text"
                placeholder="Your Name"
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                className="bg-card border-border"
                required
              />
              <Input
                type="email"
                placeholder="Your Email"
                value={contactForm.email}
                onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                className="bg-card border-border"
                required
              />
              <textarea
                placeholder="Your Message"
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                className="w-full bg-card border-border rounded-md p-2 text-sm text-foreground focus:ring-primary focus:border-primary min-h-[100px]"
                required
              />
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
                Send Message
              </Button>
            </form>
          </motion.div>
        </div>

        {/* Newsletter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-16 bg-card rounded-lg p-6 border border-primary/20"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <h3 className="font-semibold text-lg text-white">Stay Updated</h3>
              <p className="text-muted-foreground text-sm">
                Subscribe to our newsletter for the latest updates and offers.
              </p>
            </div>
            <form onSubmit={handleNewsletter} className="flex space-x-2 w-full md:w-auto">
              <Input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-background border-border w-full md:w-64"
              />
              <Button type="submit" className="bg-primary hover:bg-primary/90 whitespace-nowrap">
                Subscribe
              </Button>
            </form>
          </div>
        </motion.div>

        {/* Copyright */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="border-t border-primary/20 mt-12 pt-8 text-center"
        >
          <p className="text-muted-foreground">
            &copy; 2024 ZeeX Soft. All rights reserved. | Sharjah, UAE
          </p>
        </motion.div>
      </div>
    </footer>
  );
}
